import { DeliveryTrackerDocumentActionsComponent } from '@features/organization/components/delivery-tracker-detail/components/delivery-tracker-document-actions/delivery-tracker-document-actions.component';
import type {
  DeliveryTrackerDocument,
  DeliveryTrackerDocumentActionsInputs,
} from '@features/organization/components/delivery-tracker-detail/types/delivery-tracker-document.types';
import type { TableColumn, TranslationService } from '@kerberos-compliance/compliance-shared-ui';
import {
  ColumnType,
  DateFormatConfigurations,
  getKnownContentTypeLabel,
  localizeDate,
} from '@kerberos-compliance/compliance-shared-ui';

type DeliveryTrackerDocumentColumnsTranslation = {
  displayName: string;
  knownContentType: string;
  createdAt: string;
  actions: string;
};

/**
 * Returns the column configuration for a delivery tracker's document table: the file name opens the preview,
 * the trailing actions cell downloads every document and deletes delivered results.
 *
 * @param {TranslationService} translationService - Resolves column headers and localized values.
 * @param {(document: DeliveryTrackerDocument) => void} onPreview - Invoked when a row's file name is activated.
 * @param {DeliveryTrackerDocumentActionsInputs} actions - The callbacks behind each row's actions cell.
 * @returns {TableColumn<DeliveryTrackerDocument>[]} The table column definitions.
 */
export function getDeliveryTrackerDocumentColumnsConfig(
  translationService: TranslationService,
  onPreview: (document: DeliveryTrackerDocument) => void,
  actions: DeliveryTrackerDocumentActionsInputs,
): TableColumn<DeliveryTrackerDocument>[] {
  const columnTranslations = translationService.selectTranslateObject<DeliveryTrackerDocumentColumnsTranslation>(
    'organizations.detailPage.riskAnalysisDeliveryTab.documents.tableColumns',
  );

  return [
    {
      title: (): string | undefined => columnTranslations()?.displayName,
      key: 'displayName',
      type: ColumnType.Link,
      data: (row: DeliveryTrackerDocument): string => row.displayName,
      click: onPreview,
      tooltip: { show: false },
      order: 0,
      sort: { sortable: true },
      visible: true,
      primary: true,
    },
    {
      title: (): string | undefined => columnTranslations()?.knownContentType,
      key: 'knownContentType',
      type: ColumnType.Text,
      data: (row: DeliveryTrackerDocument): string =>
        getKnownContentTypeLabel(translationService, row.knownContentType),
      order: 1,
      sort: { sortable: true },
      visible: true,
      width: '14rem',
    },
    {
      title: (): string | undefined => columnTranslations()?.createdAt,
      key: 'createdAt',
      type: ColumnType.Date,
      data: (row: DeliveryTrackerDocument): string =>
        localizeDate(row.createdAt, translationService.activeLanguage(), DateFormatConfigurations.dateTimeFormat),
      order: 2,
      sort: { sortable: true },
      visible: true,
      width: '11rem',
    },
    {
      title: (): string | undefined => columnTranslations()?.actions,
      key: 'id',
      type: ColumnType.Custom,
      customComponent: DeliveryTrackerDocumentActionsComponent,
      customComponentInputs: (): DeliveryTrackerDocumentActionsInputs => actions,
      tooltip: { show: false },
      order: 3,
      align: 'right',
      visible: true,
      width: '7rem',
    },
  ];
}
